import type { LichessStorage } from 'lib/storage';

import type LobbyController from './ctrl';
import {
  type FormLines,
  type FormObject,
  type FormStore,
  toFormLines,
  toFormObject,
  makeStore,
} from './form';
import type { Hook } from './interfaces';

interface FilterData {
  form: FormLines;
  filter: FormObject;
}

interface Filtered {
  visible: Hook[];
  hidden: number;
}

export default class Filter {
  store: FormStore;
  data: FilterData | null;
  open = false;
  uiCacheBuster = 1; // used to force re-init of filter UI

  constructor(
    storage: LichessStorage,
    readonly root: LobbyController,
  ) {
    this.store = makeStore(storage);
    this.set(this.store.get());
  }

  toggle = () => {
    this.open = !this.open;
  };

  set = (data: FormLines | null) => {
    this.data = data && {
      form: data,
      filter: toFormObject(data),
    };
  };

  save = (form: HTMLFormElement | null) => {
    const lines = form && toFormLines(form);
    if (lines) this.store.set(lines);
    else this.store.remove();
    this.set(lines);
    this.root.onSetFilter();
  };

  filter = (hooks: Hook[]): Filtered => {
    if (!this.data) return { visible: hooks, hidden: 0 };
    const f = this.data.filter;
    const ratingRange = f.ratingRange?.split('-').map((r: string) => parseInt(r, 10));
    const seen: string[] = [];
    const visible: Hook[] = [];
    let variant: string;
    let hidden = 0;
    hooks.forEach(hook => {
      variant = hook.variant;
      if (hook.action === 'cancel') visible.push(hook);
      else {
        if (
          !f.variant?.includes(variant) ||
          !f.speed?.includes((hook.s || 1).toString() /* ultrabullet = bullet */) ||
          (f.mode?.length === 1 && f.mode[0] !== (hook.ra || 0).toString()) ||
          (f.increment?.length === 1 && f.increment[0] !== hook.i.toString()) ||
          (ratingRange && (!hook.rating || hook.rating < ratingRange[0] || hook.rating > ratingRange[1]))
        ) {
          hidden++;
        } else {
          const hash = hook.ra + variant + hook.t + hook.rating;
          if (!seen.includes(hash)) visible.push(hook);
          seen.push(hash);
        }
      }
    });
    return { visible, hidden };
  };
}
